import type { ClauseTypeAdminDTO } from '@/services/taxonomyAdminService'

/** How a clause type's working copy differs from the published version. */
export type DraftKind = 'added' | 'edited' | 'removed'

export interface DraftChange {
  code: string
  label: string
  kind: DraftKind
  /** For 'edited': the working-set field names that changed (e.g. "definition", "core"). */
  fields: string[]
}

/** Payload key → the working-set field it is published from (`type` is the label). */
const FIELD_KEYS: [payloadKey: string, field: keyof ClauseTypeAdminDTO][] = [
  ['type', 'label'],
  ['definition', 'definition'],
  ['tagWhen', 'tagWhen'],
  ['description', 'description'],
  ['positiveExample', 'positiveExample'],
  ['negativeExamples', 'negativeExamples'],
  ['distinguishFrom', 'distinguishFrom'],
  ['additionalDetails', 'additionalDetails'],
  ['needsValidityCheck', 'needsValidityCheck'],
  ['core', 'core'],
  ['color', 'color'],
  ['aliases', 'aliases'],
]
const RESERVED_KEYS = new Set([...FIELD_KEYS.map(([k]) => k), 'code', 'active', 'extraTagRules'])

/**
 * Absent, null, blank, false and empty lists all publish as "not there" (the server omits blank
 * text and a false validity flag, and a working set may hold `[]` where the payload holds nothing),
 * so they compare equal.
 */
function normalise(value: unknown): unknown {
  if (value == null || value === false || value === '') return undefined
  if (typeof value === 'string') return value.trim() === '' ? undefined : value
  if (Array.isArray(value)) return value.length === 0 ? undefined : value.map(normalise)
  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
      .map(([k, v]) => [k, normalise(v)] as const)
      .filter(([, v]) => v !== undefined)
      .sort(([a], [b]) => a.localeCompare(b))
    return entries.length ? Object.fromEntries(entries) : undefined
  }
  return value
}

const same = (a: unknown, b: unknown) => JSON.stringify(normalise(a)) === JSON.stringify(normalise(b))

/** The Extra Tag Rules a published node carries (any non-reserved top-level key). */
function publishedRules(node: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(node).filter(([k]) => !RESERVED_KEYS.has(k)))
}

/**
 * Compare one category's working set with its latest published version. The Taxonomy tab reads this
 * diff from the server (`GET /{category}/changes`, ADR 0043 D6) and falls back to this client-side
 * copy only when that endpoint cannot be read. A clause type is:
 *  - 'added' when it is active but not in the published version (or nothing is published yet);
 *  - 'removed' when the published version has it but the working copy is inactive or deleted;
 *  - 'edited' when both have it and a published field differs.
 * Inactive types that were never published are not changes.
 */
export function diffWorkingSet(
  working: ClauseTypeAdminDTO[],
  published: Record<string, unknown>[] | null,
): DraftChange[] {
  const publishedByCode = new Map((published ?? []).map((n) => [String(n.code), n]))
  const changes: DraftChange[] = []

  for (const t of working) {
    const node = publishedByCode.get(t.code)
    if (!node) {
      if (t.active) changes.push({ code: t.code, label: t.label, kind: 'added', fields: [] })
      continue
    }
    if (!t.active) {
      changes.push({ code: t.code, label: t.label, kind: 'removed', fields: [] })
      continue
    }
    const fields = FIELD_KEYS.filter(([key, field]) => !same(node[key], t[field])).map(([, field]) => field)
    if (!same(publishedRules(node), t.extraTagRules)) fields.push('extraTagRules')
    if (fields.length) changes.push({ code: t.code, label: t.label, kind: 'edited', fields })
  }

  const workingCodes = new Set(working.map((t) => t.code))
  for (const [code, node] of publishedByCode) {
    if (!workingCodes.has(code)) {
      changes.push({ code, label: String(node.type ?? code), kind: 'removed', fields: [] })
    }
  }
  return changes.sort((a, b) => a.label.localeCompare(b.label))
}
